/**
 * Exam — 试题讲解播放页
 * 复用 MicrocoursePlayer 播放 AI 生成的试题讲解课件
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MicrocoursePlayer } from '@/components/microcourse'
import { normalizeDeck } from '@ai-tutor/shared'
import type { MicrocourseProject } from '@ai-tutor/shared'
import { getLesson } from '@/api/lesson'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { ROUTES, generatePath } from '@/constants/routes'

export default function ExplanationPlayerPage() {
  const { sessionId, lessonId } = useParams<{ sessionId: string; lessonId: string }>()
  const navigate = useNavigate()

  const [project, setProject] = useState<MicrocourseProject | null>(null)
  const [title, setTitle] = useState('试题讲解')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('')

  useEffect(() => {
    if (!lessonId) return
    loadExplanation(lessonId)
  }, [lessonId])

  async function loadExplanation(id: string) {
    setLoading(true)
    setError(null)
    try {
      const lesson = await getLesson(id)
      if (!lesson) {
        setError('讲解课件不存在')
        return
      }
      setTitle(lesson.topic || '试题讲解')
      setStatus(lesson.status)

      if (lesson.status === 'ready' && lesson.deck) {
        setProject(normalizeDeck(lesson.deck, id))
      } else if (lesson.status === 'generating' || lesson.status === 'tts_pending') {
        // Poll for completion
        pollLesson(id)
      } else if (lesson.status === 'error') {
        setError(lesson.errorMessage || '生成失败')
      }
    } catch (err: any) {
      setError(err?.message ?? '加载失败')
    } finally {
      setLoading(false)
    }
  }

  function pollLesson(id: string) {
    const timer = setInterval(async () => {
      try {
        const lesson = await getLesson(id)
        setStatus(lesson.status)
        if (lesson.status === 'ready' && lesson.deck) {
          setProject(normalizeDeck(lesson.deck, id))
          clearInterval(timer)
          setLoading(false)
        } else if (lesson.status === 'error') {
          setError(lesson.errorMessage || '生成失败')
          clearInterval(timer)
          setLoading(false)
        }
      } catch {
        // ignore
      }
    }, 2000)

    // Cleanup after 60s
    setTimeout(() => clearInterval(timer), 60000)
  }

  const handleBack = () => {
    if (sessionId) {
      navigate(generatePath(ROUTES.EXAM_DIAGNOSIS, { sessionId }))
    } else {
      navigate(-1)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm text-slate-500">
          {status === 'tts_pending' ? '正在生成语音讲解...' : 'AI 正在生成讲解课件...'}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <p className="text-red-500">{error}</p>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          返回诊断报告
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          返回诊断
        </Button>
        <h1 className="text-xl font-semibold text-slate-800 truncate">{title}</h1>
      </div>

      {/* Player */}
      {project ? (
        <MicrocoursePlayer
          project={project}
          showFullscreen
          className="min-h-[500px]"
        />
      ) : (
        <div className="flex items-center justify-center h-64 text-slate-400 border rounded-xl">
          暂无讲解内容
        </div>
      )}
    </div>
  )
}
